/**
 * BẢNG MÀU + TOKEN của toàn app — nguồn DUY NHẤT của màu sắc, font và thang chữ (docs/frontend.md,
 * luật 2). File này THUẦN (không đụng `window`), nên `theme-tokens.test.ts` dựng đúng bộ token Ant
 * Design sẽ dùng và kiểm mọi cặp chữ/nền theo WCAG 2.1 AA — đổi một mã màu làm chữ khó đọc là test đỏ.
 *
 * Vì sao phải khai tay các sắc độ (`…Bg`, `…Border`, `…Hover`): Ant Design suy chúng từ màu gốc với giả
 * định màu gốc là một màu TƯƠI, sáng vừa. Màu thương hiệu ở đây cố ý ĐẬM ở chế độ sáng (để chữ màu đó đọc
 * được trên nền trắng), nên phép suy ấy ra một màu xám đục — mục menu đang chọn từng là chữ xanh trên nền
 * xám 2,6:1. Ở đây sắc nhạt = màu gốc pha với nền: luôn trong, đúng tông, và có test bảo chứng.
 */
import { theme as antdTheme, type ThemeConfig } from 'antd';
import { mix, parseColor, toHex, withAlpha } from './color';

export type ThemeMode = 'light' | 'dark';

/**
 * MỘT font cho toàn app: Mulish (nạp ở `main.tsx`, tự lưu trữ). Phần đuôi là font dự phòng trong lúc
 * Mulish chưa tải xong. Mulish KHÔNG có mũi tên, hình khối, dấu tích, chữ Hy Lạp (→ ▲ ✓ Σ…): những ký
 * tự đó sẽ rơi về font khác và lệch hẳn nét — dùng icon của `@ant-design/icons` hoặc viết bằng chữ
 * (`source-hygiene.test.ts` chặn chúng trong chữ giao diện).
 */
export const FONT_FAMILY =
  '"Mulish Variable", Mulish, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
export const CODE_FONT_FAMILY = 'ui-monospace, "Cascadia Mono", Consolas, "Courier New", monospace';

/**
 * THANG CHỮ (px). Màn hình chỉ dùng các cỡ này, lấy qua token — không gõ số:
 *   12 `fontSizeSM` chú thích · 14 `fontSize` chữ thường · 16 `fontSizeLG` nhấn / đầu thẻ ·
 *   20 `fontSizeHeading4` tiêu đề màn hình · 24 `fontSizeHeading3` con số lớn · 30 · 38.
 * Không có cỡ nào dưới 12px.
 */
export const TYPE_SCALE = [12, 14, 16, 20, 24, 30, 38] as const;

/**
 * Cỡ chữ TRƯNG BÀY — ngoại lệ DUY NHẤT ngoài thang chữ: đồng hồ lớn của màn chấm công (05/10/2026, chủ dự án
 * gửi ảnh mẫu, đồng hồ chiếm cả đầu màn). Không dùng cho chữ nào khác.
 */
export const DISPLAY_FONT_SIZE = 56;

interface Palette {
  /** Nền trang · nền thẻ/bảng/thanh bên · nền nổi (hộp thoại, menu xổ). */
  page: string;
  surface: string;
  elevated: string;
  /** Màu gốc của chữ (đen ở chế độ sáng, trắng ở chế độ tối); các cấp chữ là màu này với độ trong. */
  ink: string;
  /** Độ trong: chữ chính · chữ phụ · chú thích · mờ (icon phụ, ô đang khoá) · chữ gợi ý trong ô nhập. */
  inkAlpha: { text: number; secondary: number; tertiary: number; quaternary: number; hint: number };
  /** Màu THẤY ĐƯỢC trên màn hình (ở chế độ tối Ant Design còn pha thêm — xem `seedFor`). */
  primary: string;
  success: string;
  warning: string;
  error: string;
  /** Chữ đặt TRÊN nền màu đặc (nút chính, huy hiệu, mục đang chọn). */
  onSolid: string;
  /** Nền của tooltip; `null` = để Ant Design tự tính. */
  spotlight: string | null;
}

/**
 * Xanh mòng két + vàng đồng, giữ từ giao diện cũ.
 *  - Sáng: màu ĐẬM — làm chữ trên nền trắng được, và chữ trắng đặt lên nó cũng đọc được.
 *  - Tối: màu SÁNG — làm chữ trên nền tối được; chữ đặt lên nó vì thế phải là chữ ĐẬM (`onSolid`), không
 *    phải chữ trắng (trắng trên xanh sáng chỉ 2,7:1). Tooltip đảo thành nền sáng chữ đậm cho khớp.
 */
export const PALETTE: Record<ThemeMode, Palette> = {
  light: {
    page: '#f3f6f6',
    surface: '#ffffff',
    elevated: '#ffffff',
    ink: '#000000',
    inkAlpha: { text: 0.88, secondary: 0.68, tertiary: 0.58, quaternary: 0.45, hint: 0.55 },
    primary: '#0d6e66',
    success: '#0a6b46',
    warning: '#8a5700',
    error: '#b42318',
    onSolid: '#ffffff',
    spotlight: null,
  },
  dark: {
    page: '#0e1516',
    surface: '#151d1e',
    elevated: '#1c2627',
    ink: '#ffffff',
    inkAlpha: { text: 0.88, secondary: 0.7, tertiary: 0.58, quaternary: 0.42, hint: 0.5 },
    primary: '#38c4b3',
    success: '#3ecf8e',
    warning: '#dbb04a',
    error: '#db867e',
    onSolid: '#04201d',
    spotlight: '#e6eded',
  },
};

/**
 * LOGO DỰ ÁN SUN MUSIC (06/10/2026, chủ dự án: «Thay đổi toàn bộ thông tin web sang thông tin này») — mặt
 * trời mọc trên hai vạch sóng, vẽ bằng SVG (`public/logo-sun.svg`, `public/favicon.svg`; PNG cho PWA / iPhone
 * sinh bằng `scripts/make-brand-icons.mjs` từ cùng hình). Màu thương hiệu CỐ ĐỊNH ở cả hai chế độ: nền xanh
 * mòng két đậm, mặt trời vàng. `tile` là nền ô bo góc đặt logo lên — trùng nền của chính ảnh để mép ảnh không
 * lộ khung; `halo` là vàng của mặt trời. Trước 06/10/2026 là logo HALO RECORDS (`logo-halo.webp`, nền đen).
 */
export const BRAND_LOGO = {
  tile: '#0b3a36',
  halo: '#f5b335',
} as const;

/**
 * MÀU NHẤN THEO KHU VỰC — mỗi nhóm chức năng một tông (chủ dự án 22/09/2026: «màu sắc trực quan, không đơn
 * sắc»): Kênh & Nội dung xanh mòng két · Doanh thu vàng đồng · Công việc tím · Phân tích xanh dương · Quản trị
 * hồng · Thumbnail cam đất (thêm 26/09/2026) · xanh lá cho việc đã xong. Mỗi tông có `ink` (icon, chữ) và `tile` (ô nền nhạt sau icon); `ink` đọc
 * được trên `tile`, trên nền thẻ và nền trang (≥ 4,5:1 — `theme-tokens.test.ts` kiểm cả hai chế độ).
 */
export const ACCENT_HUES = ['teal', 'amber', 'violet', 'blue', 'rose', 'green', 'orange'] as const;
export type AccentHue = (typeof ACCENT_HUES)[number];

export interface Accent {
  ink: string;
  tile: string;
}

const ACCENT_INK: Record<ThemeMode, Record<AccentHue, string>> = {
  light: {
    teal: '#0d6e66',
    amber: '#8a5700',
    violet: '#5b3fb8',
    blue: '#1d4ed8',
    rose: '#b42345',
    green: '#0a6b46',
    orange: '#9a4408',
  },
  dark: {
    teal: '#38c4b3',
    amber: '#dbb04a',
    violet: '#b9a5f5',
    blue: '#7fb0ff',
    rose: '#f39ab0',
    green: '#3ecf8e',
    orange: '#f0a05a',
  },
};

/** Phần màu pha vào nền thẻ để ra ô nền nhạt: sáng 13% (trong, không xám), tối 20% (đủ thấy trên nền tối). */
const TILE_SHARE: Record<ThemeMode, number> = { light: 0.13, dark: 0.2 };

export function accentFor(mode: ThemeMode, hue: AccentHue): Accent {
  const ink = ACCENT_INK[mode][hue];
  return { ink, tile: mix(ink, PALETTE[mode].surface, TILE_SHARE[mode]) };
}

export function accents(mode: ThemeMode): Record<AccentHue, Accent> {
  return Object.fromEntries(ACCENT_HUES.map((hue) => [hue, accentFor(mode, hue)])) as Record<
    AccentHue,
    Accent
  >;
}

/**
 * Bóng mềm: hai lớp mờ, hơi ngả màu mực của chế độ — đủ để thẻ «nổi» khỏi nền mà không có viền đen. Chế
 * độ tối bóng gần như vô hình nên thẻ vẫn giữ một viền mảnh (`colorBorderSecondary`).
 */
const SHADOWS: Record<ThemeMode, { card: string; raised: string; overlay: string }> = {
  light: {
    card: '0 1px 2px rgba(16, 42, 45, 0.04), 0 8px 24px -12px rgba(16, 42, 45, 0.14)',
    raised: '0 2px 6px rgba(16, 42, 45, 0.06), 0 12px 32px -12px rgba(16, 42, 45, 0.2)',
    overlay: '0 6px 16px rgba(16, 42, 45, 0.08), 0 20px 48px -16px rgba(16, 42, 45, 0.28)',
  },
  dark: {
    card: '0 1px 2px rgba(0, 0, 0, 0.3), 0 8px 24px -12px rgba(0, 0, 0, 0.5)',
    raised: '0 2px 6px rgba(0, 0, 0, 0.35), 0 12px 32px -12px rgba(0, 0, 0, 0.6)',
    overlay: '0 6px 16px rgba(0, 0, 0, 0.4), 0 20px 48px -16px rgba(0, 0, 0, 0.7)',
  },
};

/** Viền: ngả nhẹ theo tông nền (xám lạnh) chứ không phải xám trung tính của Ant Design. */
const BORDERS: Record<ThemeMode, { border: string; secondary: string }> = {
  light: { border: '#d7e0e2', secondary: '#e8eef0' },
  dark: { border: '#2e3c3d', secondary: '#233031' },
};

/** Màu chuỗi số liệu cho biểu đồ — cùng thứ tự ở mọi màn hình để một kênh luôn một màu. */
export const SERIES_COLORS = {
  light: ['#0d6e66', '#b45309', '#1d4ed8', '#7c3aed', '#be123c', '#15803d', '#0e7490', '#a16207'],
  dark: ['#33c9b8', '#e9a13a', '#63a4fa', '#a78bfa', '#fb7392', '#4ecf85', '#3ed0e0', '#e07ee0'],
} as const;

/**
 * Thuật toán tối của Ant Design KHÔNG dùng nguyên màu gốc: màu chính thật sự = 85% màu gốc + 15% `#141414`,
 * và màu gốc thì không ghi đè lại được sau khi suy. Muốn màn hình ra ĐÚNG màu trong `PALETTE` thì đưa cho
 * nó màu gốc đã «đảo» phép pha ấy. (Kênh nào vượt 255 là màu đích quá sáng để đạt được — test sẽ báo.)
 */
const DARK_MIX_BACKGROUND = '#141414';
const DARK_MIX_SHARE = 0.85;

export function seedFor(target: string, mode: ThemeMode): string {
  if (mode === 'light') return target;
  const wanted = parseColor(target);
  const background = parseColor(DARK_MIX_BACKGROUND);
  const invert = (index: 0 | 1 | 2) =>
    (wanted[index] - background[index] * (1 - DARK_MIX_SHARE)) / DARK_MIX_SHARE;
  return toHex([invert(0), invert(1), invert(2)]);
}

type StatusName = 'Primary' | 'Success' | 'Warning' | 'Error' | 'Info';

/**
 * Cả họ sắc độ của một màu trạng thái. Nền nhạt = màu pha vào NỀN THẺ (trắng ở chế độ sáng, nền tối ở
 * chế độ tối) nên chữ màu đó đặt lên vẫn đọc được; «rê chuột» sáng lên một chút, «đang nhấn» đậm xuống.
 */
function shades(name: StatusName, color: string, mode: ThemeMode): Record<string, string> {
  const base = PALETTE[mode].surface;
  const share =
    mode === 'light'
      ? { bg: 0.1, bgHover: 0.18, border: 0.38, borderHover: 0.55, hover: 0.9, active: 0.85 }
      : { bg: 0.12, bgHover: 0.18, border: 0.36, borderHover: 0.5, hover: 0.85, active: 0.9 };
  const hover = mix(color, '#ffffff', share.hover);
  const active = mix(color, '#000000', share.active);
  return {
    [`color${name}Bg`]: mix(color, base, share.bg),
    [`color${name}BgHover`]: mix(color, base, share.bgHover),
    [`color${name}Border`]: mix(color, base, share.border),
    [`color${name}BorderHover`]: mix(color, base, share.borderHover),
    [`color${name}Hover`]: hover,
    [`color${name}Active`]: active,
    [`color${name}TextHover`]: hover,
    [`color${name}Text`]: color,
    [`color${name}TextActive`]: active,
  };
}

/**
 * Thẻ màu định sẵn (`<Tag color="green">`): Ant Design lấy chữ = bậc 7 trên nền bậc 1 của dải màu — vài dải
 * chỉ đạt 2,8–4,4:1. Thay chữ bằng một bậc khác CỦA CHÍNH dải màu đó (nền giữ nguyên).
 */
const PRESET_TAG_TEXT: Record<ThemeMode, Record<string, string>> = {
  light: {
    green: '#237804',
    lime: '#3f6600',
    yellow: '#876800',
    gold: '#874d00',
    orange: '#ad4e00',
    volcano: '#ad2102',
    cyan: '#006d75',
  },
  dark: { purple: '#ab7ae0', geekblue: '#7f9ef3', red: '#f37370' },
};

/**
 * Mỗi bậc màu có HAI tên token: `gold7` (mã JavaScript đọc) và `gold-7` (thành biến CSS `--ant-gold-7` mà
 * kiểu dáng của thẻ thật sự dùng). Ghi đè một tên là màn hình vẫn ra màu cũ — phải ghi cả hai.
 */
function presetTagText(mode: ThemeMode): Record<string, string> {
  return Object.fromEntries(
    Object.entries(PRESET_TAG_TEXT[mode]).flatMap(([name, color]) => [
      [`${name}7`, color],
      [`${name}-7`, color],
    ]),
  );
}

export function buildAntdTheme(mode: ThemeMode): ThemeConfig {
  const palette = PALETTE[mode];
  const ink = (alpha: number) => withAlpha(palette.ink, alpha);
  const { inkAlpha } = palette;

  return {
    algorithm: mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      fontFamily: FONT_FAMILY,
      fontFamilyCode: CODE_FONT_FAMILY,
      fontSize: 14,
      // Góc mềm: ô nhập/nút 10, thẻ 14, hộp thoại 16 (component), thẻ nhỏ 8.
      borderRadius: 10,
      borderRadiusLG: 14,
      borderRadiusSM: 8,
      borderRadiusXS: 6,
      // Nút và ô nhập cao 36 (mặc định 32): dễ bấm hơn, trang thoáng hơn; cỡ nhỏ trong bảng giữ 28.
      controlHeight: 36,
      controlHeightLG: 42,
      controlHeightSM: 28,
      colorBorder: BORDERS[mode].border,
      colorBorderSecondary: BORDERS[mode].secondary,
      boxShadow: SHADOWS[mode].raised,
      boxShadowSecondary: SHADOWS[mode].overlay,
      boxShadowTertiary: SHADOWS[mode].card,

      colorPrimary: seedFor(palette.primary, mode),
      colorInfo: seedFor(palette.primary, mode),
      colorLink: palette.primary,
      colorSuccess: seedFor(palette.success, mode),
      colorWarning: seedFor(palette.warning, mode),
      colorError: seedFor(palette.error, mode),

      colorBgLayout: palette.page,
      colorBgContainer: palette.surface,
      colorBgElevated: palette.elevated,
      ...(palette.spotlight ? { colorBgSpotlight: palette.spotlight } : {}),

      colorTextBase: palette.ink,
      colorText: ink(inkAlpha.text),
      colorTextSecondary: ink(inkAlpha.secondary),
      colorTextTertiary: ink(inkAlpha.tertiary),
      colorTextQuaternary: ink(inkAlpha.quaternary),
      colorTextPlaceholder: ink(inkAlpha.hint),
      // Icon «×» đóng hộp thoại / ngăn / thẻ: mặc định của Ant Design (45% mực) chỉ 2,9:1 trên nền trắng —
      // dùng mực phụ (≥ 4,5:1) để nút đóng đạt chuẩn như mọi icon khác.
      colorIcon: ink(inkAlpha.secondary),
      colorIconHover: ink(inkAlpha.text),
      colorTextLightSolid: palette.onSolid,
      // Dấu tích của ô chọn, núm nút gạt… nằm trên nền màu chính → cùng luật với chữ trên nền màu đặc.
      colorWhite: palette.onSolid,

      ...shades('Primary', palette.primary, mode),
      ...shades('Info', palette.primary, mode),
      ...shades('Success', palette.success, mode),
      ...shades('Warning', palette.warning, mode),
      ...shades('Error', palette.error, mode),
      colorErrorBgFilledHover: mix(palette.error, palette.surface, mode === 'light' ? 0.24 : 0.26),
      colorErrorBgActive: mix(palette.error, palette.surface, mode === 'light' ? 0.3 : 0.32),
      colorLinkHover: mix(palette.primary, '#ffffff', mode === 'light' ? 0.9 : 0.85),
      colorLinkActive: mix(palette.primary, '#000000', mode === 'light' ? 0.85 : 0.9),
      ...presetTagText(mode),
    },
    components: {
      Layout: { siderBg: palette.surface, headerBg: palette.surface, headerPadding: '0 20px' },
      // Mục menu là những VIÊN bo tròn cách nhau, icon 16px ở cả hai trạng thái của thanh bên.
      Menu: {
        itemBg: 'transparent',
        subMenuItemBg: 'transparent',
        iconSize: 16,
        collapsedIconSize: 16,
        itemBorderRadius: 10,
        subMenuItemBorderRadius: 10,
        itemMarginInline: 8,
        itemMarginBlock: 3,
        itemHeight: 38,
        iconMarginInlineEnd: 10,
      },
      // Đầu bảng nhạt, không vạch dọc; (mũi tên sắp xếp không có token công khai — chỉnh trong `global.css`.)
      Table: {
        cellPaddingBlockSM: 6,
        cellPaddingInlineSM: 8,
        headerSplitColor: 'transparent',
        headerBorderRadius: 12,
      },
      // Tiêu đề thẻ LUÔN 16px, kể cả thẻ cỡ nhỏ (thẻ nhỏ chỉ gọn phần lề) — một bậc chữ cho mọi đầu thẻ.
      Card: { headerFontSize: 16, headerFontSizeSM: 16, borderRadiusLG: 14 },
      // Chữ cái thay ảnh đại diện: mặc định của Ant Design là 18px — không có trong thang chữ.
      Avatar: { textFontSize: 16, textFontSizeLG: 20, textFontSizeSM: 14 },
      // Nút chính đậm chữ, có bóng màu của chính nó; nút thường không bóng.
      Button: {
        fontWeight: 600,
        defaultShadow: 'none',
        primaryShadow: `0 6px 16px -8px ${withAlpha(palette.primary, mode === 'light' ? 0.55 : 0.4)}`,
        dangerShadow: `0 6px 16px -8px ${withAlpha(palette.error, mode === 'light' ? 0.45 : 0.4)}`,
      },
      Tag: { borderRadiusSM: 999 },
      Segmented: { borderRadius: 10, borderRadiusSM: 8 },
      Modal: { borderRadiusLG: 16 },
      Popover: { borderRadiusLG: 12 },
      Dropdown: { borderRadiusLG: 12 },
      Alert: { borderRadiusLG: 12 },
      Collapse: { borderRadiusLG: 12 },
      Checkbox: { borderRadiusSM: 6 },
      // Công tắc cao 24px (mặc định 22px): đủ mốc đích bấm tối thiểu trên điện thoại (WCAG 2.5.8) mà bộ
      // soát `responsive-audit.ts` canh.
      Switch: { trackHeight: 24, handleSize: 20, trackMinWidth: 46 },
      Tooltip: { borderRadius: 8 },
    },
  };
}
